import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { classNames } from "../../helpers/classNames";

/**
 * The redesign's button (docs/design/HANDOFF.md, "Buttons").
 *
 * - primary: filled md-pink-dark, hover md-pink-darker (white text passes 4.5:1).
 * - secondary: white with a 2px md-control border.
 * - text: no box, rose text, for links inside copy and card actions.
 * - destructive: filled red-700, only for things that can't be undone.
 *
 * Never shorter than 44px. `busy` shows a spinner and keeps the label (pass a
 * verb: "Preparing PDF"), and disables the button. `to` renders a router Link,
 * `href` an anchor, otherwise a <button>.
 */

const VARIANTS = {
  primary: "bg-md-pink-dark text-white hover:bg-md-pink-darker",
  secondary: "bg-white text-md-dark border-2 border-md-control hover:border-md-control-input",
  text: "bg-transparent text-md-pink-dark hover:text-md-pink-darker underline-offset-2 hover:underline",
  destructive: "bg-red-700 text-white hover:bg-red-800",
};

const SIZES = {
  // Bottom bar and side panel primaries.
  lg: "min-h-[54px] px-5 text-[17px] md:text-lg rounded-xl",
  // Pop-up buttons, card actions.
  md: "min-h-[50px] px-5 text-base rounded-xl",
  // Chips, secondary actions beside a photo.
  sm: "min-h-[44px] px-4 text-[15px] rounded-full",
};

const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    busy = false,
    disabled = false,
    fullWidth = false,
    icon = null,
    iconRight = null,
    to,
    href,
    type = "button",
    className,
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || busy;
  const classes = classNames(
    "inline-flex items-center justify-center gap-2 font-semibold leading-none transition-colors duration-150",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink focus-visible:ring-offset-2",
    variant === "text" ? "min-h-[44px] px-1 text-[15px] rounded-md" : SIZES[size] || SIZES.md,
    VARIANTS[variant] || VARIANTS.primary,
    fullWidth && "w-full",
    isDisabled && "opacity-60 pointer-events-none",
    className
  );

  const content = (
    <>
      {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : icon}
      {children && <span>{children}</span>}
      {!busy && iconRight}
    </>
  );

  if (to && !isDisabled) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href && !isDisabled) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button ref={ref} type={type} className={classes} disabled={isDisabled} aria-busy={busy || undefined} {...rest}>
      {content}
    </button>
  );
});

export default Button;
